import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import confetti from "canvas-confetti";
import api from "@/lib/api";
import DealCard from "@/components/DealCard";
import SaleMarquee from "@/components/SaleMarquee";
import AdBanner from "@/components/AdBanner";
import DealOfWeek from "@/components/DealOfWeek";
import NewsletterSignup from "@/components/NewsletterSignup";
import Seo from "@/components/Seo";
import { Lightning, MagnifyingGlass, Rocket } from "@phosphor-icons/react";

const HERO = "https://images.unsplash.com/photo-1672872476232-da16b45c9001?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function PlatformStore() {
  const { slug } = useParams();
  const [tenant, setTenant] = useState(null);
  const [categories, setCategories] = useState([]);
  const [links, setLinks] = useState([]);
  const [ads, setAds] = useState([]);
  const [search, setSearch] = useState("");
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(async (q = "") => {
    try {
      const t = await api.get(`/tenants/${slug}`);
      setTenant(t.data);
      const [c, l, a] = await Promise.all([
        api.get("/categories", { params: { tenant: slug } }),
        api.get("/links", { params: { tenant: slug, ...(q ? { search: q } : {}) } }),
        api.get("/ads", { params: { tenant: slug, placement: "home" } }),
      ]);
      setCategories(c.data); setLinks(l.data); setAds(a.data);
    } catch { setNotFound(true); }
  }, [slug]);

  useEffect(() => {
    load();
    const t = setTimeout(() => {
      confetti({ particleCount: 90, spread: 80, origin: { y: 0.3 }, colors: ["#FF007F", "#00FFFF", "#FFD600", "#39FF14"] });
    }, 500);
    return () => clearTimeout(t);
  }, [load]);

  if (notFound) return <div className="min-h-[60vh] flex items-center justify-center text-zinc-400 font-display uppercase">Storefront not found</div>;
  if (!tenant) return <div className="min-h-[60vh] flex items-center justify-center text-[#FF007F] font-display uppercase animate-pulse">Loading…</div>;

  return (
    <div>
      <Seo title={tenant.brand_name} description={tenant.tagline} path={`/site/${slug}`} />
      <section className="relative overflow-hidden">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/75" />
        <div className="relative max-w-7xl mx-auto px-6 py-20">
          <div className="inline-flex items-center gap-2 border border-[#FF007F] px-3 py-1 mb-5">
            <Lightning size={14} weight="fill" color="#FFD600" />
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#FFD600]">Storewide Sale</span>
          </div>
          <h1 className="font-display text-4xl sm:text-6xl font-black uppercase tracking-tighter leading-none">{tenant.brand_name}</h1>
          <p className="text-lg text-zinc-300 max-w-2xl mt-4">{tenant.tagline}</p>
          <form onSubmit={(e) => { e.preventDefault(); load(search); }} className="mt-8 flex max-w-xl">
            <div className="relative flex-1">
              <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search deals…"
                className="w-full bg-zinc-900/90 border border-zinc-700 focus:border-[#00FFFF] outline-none pl-10 pr-3 py-3 text-white" />
            </div>
            <button type="submit" className="px-6 bg-[#FF007F] text-white font-bold uppercase tracking-widest border border-[#FF007F] hover:bg-[#09090B] hover:text-[#FF007F] transition-colors">Go</button>
          </form>
        </div>
      </section>

      <SaleMarquee />
      {ads.length > 0 && <AdBanner placement="home" tenant={slug} />}

      <DealOfWeek tenant={slug} />

      <section className="max-w-7xl mx-auto px-6 py-14">
        <h2 className="font-display text-2xl sm:text-4xl font-black uppercase tracking-tight mb-8">Shop by <span className="text-[#FF007F]">Category</span></h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {categories.map((c) => (
            <Link key={c.id} to={`/site/${slug}/category/${c.slug}`} className="group relative h-32 overflow-hidden border border-zinc-800 hover:border-[#FF007F] transition-all bg-[#18181B] flex items-end p-4">
              <div>
                <h3 className="font-display font-bold uppercase leading-tight">{c.name}</h3>
                <span className="font-mono text-xs text-[#00FFFF]">{c.link_count} deals</span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-16">
        <div className="flex items-center gap-3 mb-8">
          <Rocket size={28} weight="fill" color="#FFD600" />
          <h2 className="font-display text-2xl sm:text-4xl font-black uppercase tracking-tight">Hot <span className="text-[#00FFFF]">Deals</span></h2>
        </div>
        {links.length === 0 ? (
          <div className="border border-dashed border-zinc-800 p-12 text-center text-zinc-400">No deals yet on this store.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {links.map((l, i) => <DealCard key={l.id} link={l} index={i} />)}
          </div>
        )}
      </section>

      <section className="max-w-3xl mx-auto px-6 pb-12">
        <div className="border border-zinc-800 bg-[#18181B] p-6 text-center">
          <NewsletterSignup tenant={slug} />
        </div>
      </section>

      <p className="text-center text-xs text-zinc-600 font-mono pb-10">Powered by Affiliated Connections</p>
    </div>
  );
}
